/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Text from "./Text";
import Title from "./Title";
import { Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalImage, ModalTitle } from "./Modal";
import { useAssetsSrc } from "../../hooks/useAssetsSrc";
import FormatPrice from "../formatters/FormatPrice";
import BookingStepItem from "../booking/steps/BookingStepItem";
import Icon from "./Icon";
import Flex from "./Flex";
import Anchor from "./Anchor";
import T from "./Translation";
import PluginCardIncludes from "./PluginCardIncludes";
import { htmlToPlainText, sanitizeHtml } from "../../utils/sanitize";
import Stack from "./Stack";
import Badge from "./Badge";

const StyledPluginCard = styled((props) => <BookingStepItem {...props} />)`
  position: relative;
  padding: 8px;
  break-inside: avoid;
  border-radius: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

   ${({ $disabled, }) => $disabled && `
    pointer-events: none;
    opacity: 0.5;
   `}
`;

const CardMedia = styled(Flex)`
  position: relative;
  width: 100%;
  border-radius: 8px;
  flex-shrink: 0;
  overflow: hidden;
  height: 228px;
  background-color: rgba(${({ theme }) => theme.colors.border.rgb}, 0.5);

  @media (max-width: 320px) {
    height: 200px;
  }
`;

const CardBadgeOverlay = styled(Flex)`
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 1;
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  max-height: 500px;
  object-fit: cover;
`;

const CardIcon = styled.img`
  width: 120px;
  height: 120px;
  object-fit: contain;
`;

const CardInfo = styled(Flex)`
  height: 100%;
`;

const CardTitleAndPrice = styled(Flex)`
  width: 100%;
`;

const CardTitle = styled(Title)`
  min-height: 2lh;
`;

const CardDescription = styled(Text)`
  min-height: 2lh;
`;

const CardHeader = styled(Flex)`
  margin-bottom: auto;
`;

const CardAdditional = styled(Stack)`
  border-top: 1px solid rgba(${({ theme }) => theme.colors.border.rgb});
  padding-top: 1rem;  
`;

const CardPrice = styled(Text)`
  flex-shrink: 0;
`

const ViewDetailsContainer = styled(Flex)`
  min-height: 2rem;
`;


const ViewDetailsText = styled((props) => <Anchor {...props} />)`
  color: rgba(${({ theme }) => theme.colors.accent.rgba});
  white-space: nowrap;

  @media (hover: hover) {
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ModalHeading = ({ name, price, currency }) => (
  <CardTitleAndPrice $justify="space-between" $gap="1rem">
    <ModalTitle>{name}</ModalTitle>
    {!!price && (
      <CardPrice size="xlarge" weight="bold" color="dark">
        <FormatPrice value={price} currency={currency} />
      </CardPrice>
    )}
  </CardTitleAndPrice>
);

const PluginCard = ({
  name,
  price,
  currency,
  description,
  picture,
  fallbackIcon = <Icon.Bag size="90px" strokeWidth={3}/>,
  isSelected,
  includes,
  includeLimit = 2,
  stickyInclude,
  beforeFooter,
  footer,
  modalFooter,
  modalStickyInclude,
  disabled = false,
  reserveDescription = false,
  closeOnSelect = false,
  badge,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (closeOnSelect && isSelected) setIsModalOpen(false);
  }, [closeOnSelect, isSelected]);

  const image = useAssetsSrc(picture);
  const hasPhoto = picture && !picture.endsWith('.svg');
  const hasIncludes = includes?.length > 0;
  const showViewDetails = hasIncludes;
  const showReadMore = !hasIncludes && !!description;

  return (
    <>
      <StyledPluginCard selected={isSelected} $disabled={disabled}>
        {badge && (
          <CardBadgeOverlay $align="center" $gap="0.5rem">
            <Badge title={badge} color="white" />
          </CardBadgeOverlay>
        )}
        <CardMedia $gap="0" $justify="center" $align="center">
          {picture
            ? picture.endsWith('.svg')
              ? <CardIcon src={image} alt={name} />
              : <CardImage src={image} alt={name} loading="lazy" decoding="async" />
            : fallbackIcon
          }
        </CardMedia>

        <CardInfo $direction="column" $justify="space-between" $gap="1rem" $padding="0.5rem">
          <Flex $direction="column" $gap="1rem">
            <CardHeader $direction="column" $gap="0.5rem">
              <CardTitleAndPrice $justify="space-between" $gap="1rem">
                <CardTitle size="h3" lineClamp={2}>{name}</CardTitle>
                {!!price && (
                  <CardPrice size="xlarge" weight="bold" color="dark">
                    <FormatPrice value={price} currency={currency} />
                  </CardPrice>
                )}
              </CardTitleAndPrice>
              <Stack $gap="0.25rem" $align="start">
                {(description || reserveDescription) && (
                  <CardDescription lineClamp={2} onReadMoreClick={showReadMore ? () => setIsModalOpen(true) : undefined}>
                    {description ? htmlToPlainText(description) : ""}
                  </CardDescription>
                )}
              </Stack>
            </CardHeader>
            
            {(includes?.length > 0 || stickyInclude) && (
              <CardAdditional>
                <PluginCardIncludes items={includes} limit={includeLimit} truncate={1} stickyItem={stickyInclude} />
              </CardAdditional>
            )}
          </Flex>


          <Flex $direction="column" $gap="0.5rem">
            {(beforeFooter || showViewDetails) && (
              <Flex $align="center" $justify={beforeFooter ? "space-between" : "center"} $gap="0.5rem">
                {beforeFooter}
                {showViewDetails &&
                  <ViewDetailsContainer $align="center">
                    <ViewDetailsText underline="hover" onClick={() => setIsModalOpen(true)}><T>View details</T></ViewDetailsText>
                  </ViewDetailsContainer>
                }
              </Flex>
            )}
            {footer}
          </Flex>
        </CardInfo>
      </StyledPluginCard>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalContent>
          {picture ? (
            <>
              <ModalCloseButton />
              <ModalImage src={image} alt={name} />
            </>
          ) : (
            <ModalHeader>
              <ModalHeading name={name} price={price} currency={currency} />
            </ModalHeader>
          )}
          <ModalBody>
            <Flex $direction="column" $gap="1rem">
              {(hasPhoto || description) && (
                <CardHeader $direction="column" $gap="0.5rem" $bordered={includes?.length > 0}>
                  {hasPhoto && <ModalHeading name={name} price={price} currency={currency} />}
                  {description && <Text dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }} />}
                </CardHeader>
              )}
              <CardAdditional>
                {(includes?.length > 0 || stickyInclude) && <PluginCardIncludes items={includes} stickyItem={modalStickyInclude ?? stickyInclude} />}
              </CardAdditional>
            </Flex>
          </ModalBody>
          {modalFooter && (
            <ModalFooter>
              {modalFooter}
            </ModalFooter>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default PluginCard;